import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "components/app/dialog/dialog";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { X, Plus, Play, Loader } from "lucide-react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { RequestApi, RouteApi } from "utils";
import { Service } from "utils/services";
import { AppLog } from "services";

import { useAppSelector } from "store/store";
import axios from "axios";
import { RequestApiDisableHead } from "utils/requestApi";
import { Snackbar } from "components";
import RegionsPlugin, { Region } from "wavesurfer.js/dist/plugins/regions.js";
import WaveSurfer from "wavesurfer.js/dist/wavesurfer";

interface DialogProps {
  onUploadSuccess: (value: any) => void;
  isDialogOpen: boolean;
  setIsDialogOpen: (value: boolean) => void;
  soundData: any;
}

export default function DialogTrimMusic({ isDialogOpen, setIsDialogOpen, onUploadSuccess, soundData }: DialogProps) {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const regionsPluginRef = useRef<RegionsPlugin | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [soundUrl, setSoundUrl] = useState<string | null>(null);
  const [currentTimeSound, setCurrentTimeSound] = useState<number>(0);
  const [startingTimeSound, setStartingTimeSound] = useState<number>(0);
  const [endingTimeSound, setEndingTimeSound] = useState<number>(0);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  const workerWaveSurfer = async () => {
    if (!waveformRef.current) return;
    if (!soundUrl) return;

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#94a3b8",
      progressColor: "#2563eb",
      cursorColor: "#ef4444",
      barWidth: 3,
      barRadius: 2,
      height: 80,
      backend: "MediaElement",
      normalize: true,
      hideScrollbar: true,
      dragToSeek: true,
      autoCenter: true,
      barHeight: 0.5,
    });

    wavesurferRef.current = wavesurfer;

    await wavesurfer.on("ready", () => {
      setIsReady(true);

      const duration = wavesurfer.getDuration();
      console.log("Total Duration:", duration);

      const region = regionsPlugin.addRegion({
        start: 0,
        end: duration,
        color: "rgba(255, 0, 0, 0.3)",
        drag: true,
        resize: true,
      });

      displayRegionTimes(region);
    });

    const regionsPlugin = wavesurfer.registerPlugin(RegionsPlugin.create());
    regionsPluginRef.current = regionsPlugin;

    // Event listeners for region updates
    await regionsPlugin.on("region-updated", (region: any) => {
      console.log("Region Updated:", region);
      displayRegionTimes(region); // Ensure the function expects start & end
    });

    await regionsPlugin.on("region-clicked", (region: any, e: any) => {
      e.stopPropagation(); // Prevent waveform click event
      region.play(true);
      setIsPlaying(true);
    });

    await wavesurfer.on("audioprocess", () => {
      updateCurrentTime();
    });

    await wavesurfer.load(soundUrl);

    return () => {
      console.log("Destroying Wavesurfer instance...");
      wavesurfer.destroy();
    };
  };

  const displayRegionTimes = (region: Region) => {
    if (!waveformRef.current || !wavesurferRef.current) return;

    const totalDuration = wavesurferRef.current.getDuration();
    const container = waveformRef.current;

    let startTimeLabel = container.querySelector(".time-label.start-time") as HTMLDivElement;
    let endTimeLabel = container.querySelector(".time-label.end-time") as HTMLDivElement;
    let currentTimeLabel = container.querySelector(".time-label.current-time") as HTMLDivElement;

    // Create the labels if they don't exist
    if (!startTimeLabel) {
      startTimeLabel = document.createElement("div");
      startTimeLabel.className = "time-label start-time float-start";
      container.appendChild(startTimeLabel);
    }

    if (!endTimeLabel) {
      endTimeLabel = document.createElement("div");
      endTimeLabel.className = "time-label end-time float-end";
      container.appendChild(endTimeLabel);
    }

    if (!currentTimeLabel) {
      currentTimeLabel = document.createElement("div");
      currentTimeLabel.className = "time-label current-time";
      container.appendChild(currentTimeLabel);
    }

    let parentDiv = container.querySelector(".duration-layout") as HTMLDivElement;

    if (!parentDiv) {
      parentDiv = document.createElement("div");
      parentDiv.className = "duration-layout";
      container.appendChild(parentDiv);
    }

    // Append the labels to the parent div
    parentDiv.appendChild(startTimeLabel);
    parentDiv.appendChild(currentTimeLabel);
    parentDiv.appendChild(endTimeLabel);

    // Apply justify-between using CSS Flexbox
    parentDiv.style.display = "flex";
    parentDiv.style.justifyContent = "space-between";

    // Set the start and end times for the region
    startTimeLabel.innerText = `${region.start.toFixed(2)}s`;
    endTimeLabel.innerText = `${region.end.toFixed(2)}s`;

    const containerWidth = container.clientWidth;
    const regionStartPosition = (region.start / totalDuration) * containerWidth;
    const regionEndPosition = (region.end / totalDuration) * containerWidth;

    // **Update state instead of modifying styles directly**
    setStartingTimeSound(regionStartPosition);
    setEndingTimeSound(containerWidth - regionEndPosition);

    startTimeLabel.style.fontWeight = `bold`;
    endTimeLabel.style.fontWeight = `bold`;

    // Update current time label based on the audio's progress
    const updateCurrentTime = () => {
      if (!wavesurferRef.current) return;

      const currentTime = wavesurferRef.current.getCurrentTime(); // Get current time of the audio
      if (currentTime <= totalDuration) {
        currentTimeLabel.innerText = `${currentTime.toFixed(2)}s`;

        // **Update state instead of modifying styles directly**
        const currentTimePosition = (currentTime / totalDuration) * containerWidth;
        setCurrentTimeSound(currentTimePosition);
      } else {
        currentTimeLabel.style.display = "none"; // Hide if the audio is done
      }
    };

    // Attach listener to update current time while playing
    wavesurferRef.current.on("audioprocess", updateCurrentTime);
  };

  const togglePlayPause = () => {
    if (!wavesurferRef.current) return;

    if (wavesurferRef.current.isPlaying()) {
      wavesurferRef.current.pause();
      setIsPlaying(false);
    } else {
      wavesurferRef.current.play();
      setIsPlaying(true);
    }
  };

  const updateCurrentTime = () => {
    if (!wavesurferRef.current || !regionsPluginRef.current) return;

    const container: any = waveformRef.current;
    const currentTime = wavesurferRef.current.getCurrentTime();

    const region = regionsPluginRef.current.getRegions()[0]; // Get first region
    if (!region) return;

    let currentTimeLabel = container?.querySelector(".time-label.current-time") as HTMLDivElement;

    if (currentTimeLabel) {
      if (currentTime >= region.start && currentTime <= region.end) {
        const containerWidth = container.clientWidth;
        const currentTimePosition = ((currentTime - region.start) / (region.end - region.start)) * containerWidth;
        setCurrentTimeSound(currentTimePosition);
      }
    }
  };

  const skipTime = (seconds: number) => {
    if (!wavesurferRef.current) return;

    const currentTime = wavesurferRef.current.getCurrentTime();
    const duration = wavesurferRef.current.getDuration();

    let newTime = currentTime + seconds;

    // Ensure new time is within bounds
    if (newTime < 0) newTime = 0;
    if (newTime > duration) newTime = duration;

    wavesurferRef.current.seekTo(newTime / duration);
  };

  useEffect(() => {
    if (isDialogOpen) {
      setSoundUrl(soundData?.sound_url);
    }
  }, [soundData?.sound_url, isDialogOpen]);

  useEffect(() => {
    if (soundUrl && isDialogOpen) {
      workerWaveSurfer();
    }
  }, [soundUrl, isDialogOpen]);

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent isShowBtnXClose={false} className="min-w-[560px] p-0">
        <DialogHeader className="mt-4 border-b-2 pb-4">
          <DialogTitle className="text-center">Trim Music</DialogTitle>
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
            onClick={() => {
              setIsDialogOpen(false);
              setSoundUrl(null);
            }}
          >
            <X />
          </div>
        </DialogHeader>
        <div className="flex flex-col items-center w-full max-w-md mx-auto p-4 shadow-lg rounded-lg">
          <div className="w-full">
            <div id="waveform" ref={waveformRef} className="w-full my-4 border rounded-md relative" />
            <div className="flex justify-between">
              <span>{formatTime(startingTimeSound)}</span>
              <span>{formatTime(currentTimeSound)}</span>
              <span>{formatTime(endingTimeSound)}</span>
            </div>
          </div>
          {!isReady && <p className="text-red-500">Loading waveform...</p>}
          <div className="flex justify-between">
            <button onClick={() => skipTime(-10)}>⏪ 10s</button>
            <button onClick={togglePlayPause} className="px-6 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-200" disabled={!isReady}>
              {isPlaying ? "Pause" : "Play"}
            </button>
            <button onClick={() => skipTime(10)}>10s ⏩</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
